<template lang="pug">
section.billing-settings(v-loading="loading")
    //- Trial Status Banner
    .trial-banner(v-if="trialStatus.isInTrial" :class="trialStatusClass")
        i.el-icon-fa-clock.fas
        .trial-info
            span.trial-title {{ trialStatusTitle }}
            span.trial-subtitle {{ trialStatusSubtitle }}

    template(v-if="billingProfile || paysByInvoice")
        //- Bank transfer: the invoice is the payment method, there is no card to manage
        .section(v-if="paysByInvoice")
            h4.section-title {{ $t('Payment Method') }}
            .payment-method-card
                .card-info
                    .card-icon
                        i.el-icon-fa-file-invoice.fas
                    .card-details
                        span.card-brand {{ $t('On invoice') }}
                        span.card-number {{ $t('You receive each invoice by email and find it below. Please transfer the amount by the due date stated on it.') }}

        //- Payment Method Section (full width)
        .section(v-else)
            h4.section-title {{ $t('Payment Method') }}

            //- Show current payment method if we have one
            template(v-if="hasPaymentMethod && !changingCard")
                .payment-method-card
                    .card-info
                        .card-icon
                            i(:class="paymentMethodIcon")
                        .card-details
                            span.card-brand {{ paymentMethodLabel }}
                            span.card-number {{ $t('ending in') }} {{ billingProfile.card_last_four }}
                            span.card-expiry(v-if="cardExpiry" :class="{ 'is-warning': cardExpiresSoon, 'is-expired': cardExpired }")
                                i.el-icon-fa-exclamation-triangle.fas(v-if="cardExpiresSoon || cardExpired")
                                span(v-if="cardExpired") {{ $t('expired {date}', { date: cardExpiry }) }}
                                span(v-else) {{ $t('expires {date}', { date: cardExpiry }) }}
                    .card-actions
                        el-button(
                            type="text"
                            icon="el-icon-fa-sync-alt fas"
                            @click="startChangingCard"
                        ) {{ $t('Change payment method') }}

            //- Show "No payment method" if we don't have one
            template(v-else-if="!hasPaymentMethod && !showStripeForm")
                .no-payment-method
                    i.el-icon-fa-credit-card.fas
                    .no-payment-info
                        span.no-payment-title {{ $t('No payment method configured') }}
                        span.no-payment-subtitle {{ $t('Add a credit card or a SEPA direct debit mandate to enable automatic billing.') }}
                    el-button(
                        type="primary"
                        icon="el-icon-fa-plus fas"
                        @click="startAddingCard"
                    ) {{ $t('Add payment method') }}

            //- Stripe payment input form
            template(v-if="showStripeForm")
                .stripe-form
                    el-form-item(:label="$t('Payment details')" :error="stripeValidationError" :class="{ 'is-success': stripePaymentComplete && !stripeValidationError }")
                        .stripe-card-wrapper(:class="{ 'has-error': stripeValidationError, 'is-complete': stripePaymentComplete && !stripeValidationError }")
                            .stripe-loading(v-if="stripeLoading")
                                i.el-icon-loading
                                span {{ $t('Loading payment form...') }}
                            .stripe-card-element(ref="paymentElement" v-show="!stripeLoading")

                    .stripe-actions
                        el-button(@click="cancelChangingCard") {{ $t('Cancel') }}

                    //- p.save-hint(v-if="stripePaymentComplete && !stripeValidationError")
                    //-     i.el-icon-fa-info-circle.fas
                    //-     span {{ $t('Click the Save button to save your payment method.') }}

        //- Billing Information Section

        .section
            h4.section-title {{ $t('Billing Information') }}

            //- Which invoice this web presence lands on - shown in both cases

            el-alert.billing-scope-notice(
                :closable="false"
                type="info"
                show-icon
                :title="billingScopeNotice"
            )

            .address-card-wrapper(v-if="hasBillingAddress || showBillingAddressForm")
                el-button.remove-btn(
                    plain
                    circle
                    size="mini"
                    type="danger"
                    icon="el-icon-fa-trash-alt fas"
                    @click="removeBillingAddress"
                )
                AddressCardInput(
                    :form="settingsForm"
                    formPath="billingAddress"
                    propPath="billingAddress"
                    :fields="['gender', 'first_name', 'last_name', 'email', 'company', 'tax_id', 'street', 'zipcode', 'city', 'country']"
                    :placeholders="emailPlaceholder"
                )

            //- Fallback panel when no billing address

            .fallback-panel(v-else)
                .flex-row
                    .info.grow
                        .fallback-label {{ $t('Fallback address') }}
                        .fallback-name(v-if="fallbackName") {{ fallbackName }}
                        .fallback-address(v-if="fallbackAddressOneLiner") {{ fallbackAddressOneLiner }}
                        .no-address(v-if="!fallbackName && !fallbackAddressOneLiner") {{ $t('No address available') }}
                    .actions
                        el-button(
                            plain
                            circle
                            size="mini"
                            type="primary"
                            icon="el-icon-fa-pencil-alt fas"
                            @click="addBillingAddress"
                        )

        //- Invoice History Section

        .section(v-if="invoices.length")
            h4.section-title {{ $t('Invoices') }}

            el-table.invoice-table(:data="invoices" size="mini" v-loading="loadingInvoices")
                el-table-column(:label="$t('Invoice')" prop="invoice_number" min-width="150")
                el-table-column(:label="$t('Period')" min-width="180")
                    template(slot-scope="scope")
                        span {{ invoicePeriodLabel(scope.row) }}
                el-table-column(:label="$t('Total')" align="right" width="120")
                    template(slot-scope="scope")
                        span {{ formatPrice(scope.row.total) }}
                el-table-column(:label="$t('Status')" width="140")
                    template(slot-scope="scope")
                        el-tag(size="mini" :type="invoiceStatusType(scope.row.status)") {{ $t('invoice_status.' + scope.row.status) }}
                        //- Why the bank said no, in the customer's own words for it.
                        .invoice-reason(v-if="scope.row.failure_code") {{ failureText(scope.row) }}
                el-table-column(width="60" align="right")
                    template(slot-scope="scope")
                        el-button(
                            type="text"
                            icon="el-icon-fa-file-pdf fas"
                            :title="$t('Download PDF')"
                            @click="openInvoicePdf(scope.row)"
                        )

        //- p.save-hint(v-if="hasPendingBillingChanges")
        //-     i.el-icon-fa-info-circle.fas
        //-     span {{ $t('Click the Save button to save your changes.') }}

    //- No billing profile - offer to enable auto-billing

    .section.no-billing-profile(v-else-if="!loading")
        .no-payment-method
            i.el-icon-fa-file-invoice-dollar.fas
            .no-payment-info
                span.no-payment-title {{ $t('Auto-billing not enabled') }}
                span.no-payment-subtitle {{ $t('Enable automatic billing to manage your subscription and payment method.') }}
            el-button(
                type="primary"
                icon="el-icon-fa-check fas"
                :loading="enabling"
                @click="enableAutoBilling"
            ) {{ $t('Enable auto-billing') }}
</template>

<i18n>
{
    "en": {
        "{n} days remaining": "{n} days remaining",
        "visa": "Visa",
        "mastercard": "Mastercard",
        "amex": "American Express",
        "discover": "Discover",
        "diners": "Diners Club",
        "jcb": "JCB",
        "unionpay": "UnionPay",
        "sepa_debit": "SEPA direct debit",
        "expires {date}": "expires {date}",
        "expired {date}": "expired {date}"
    },
    "de": {
        "On invoice": "Auf Rechnung",
        "You receive each invoice by email and find it below. Please transfer the amount by the due date stated on it.": "Sie erhalten jede Rechnung per E-Mail und finden sie unten. Bitte überweisen Sie den Betrag bis zum darauf genannten Fälligkeitsdatum.",
        "Payment Method": "Zahlungsmethode",
        "ending in": "endet auf",
        "Change payment method": "Zahlungsmethode ändern",
        "No payment method configured": "Keine Zahlungsmethode hinterlegt",
        "Add a credit card or a SEPA direct debit mandate to enable automatic billing.": "Hinterlegen Sie eine Kreditkarte oder ein SEPA-Lastschriftmandat, um die automatische Abrechnung zu aktivieren.",
        "Add payment method": "Zahlungsmethode hinzufügen",
        "Payment details": "Zahlungsdaten",
        "Loading payment form...": "Zahlungsformular wird geladen...",
        "Cancel": "Abbrechen",
        "Save payment method": "Zahlungsmethode speichern",
        "Billing Information": "Rechnungsinformationen",
        "Company": "Firma",
        "VAT ID": "USt-IdNr.",
        "Invoice Email": "Rechnungs-E-Mail",
        "To update billing information, please edit your account details in the Basic settings tab.": "Um die Rechnungsinformationen zu aktualisieren, bearbeiten Sie bitte Ihre Kontodaten im Tab \"Basis\".",
        "Trial Period": "Testphase",
        "{n} days remaining": "{n} Tage verbleibend",
        "Trial Expired": "Testphase abgelaufen",
        "Please add a payment method to continue using the service.": "Bitte fügen Sie eine Zahlungsmethode hinzu, um den Service weiter zu nutzen.",
        "Payment method saved successfully": "Zahlungsmethode erfolgreich gespeichert",
        "Settings could not be saved": "Einstellungen konnten nicht gespeichert werden",
        "The payment details could not be saved.": "Die Zahlungsdaten konnten nicht gespeichert werden.",
        "Failed to load billing information": "Fehler beim Laden der Rechnungsinformationen",
        "Please enter your payment details.": "Bitte geben Sie Ihre Zahlungsdaten ein.",
        "No billing profile found": "Kein Abrechnungsprofil gefunden",
        "Auto-billing is not enabled for this CMS.": "Die automatische Abrechnung ist für dieses CMS nicht aktiviert.",
        "Auto-billing not enabled": "Automatische Abrechnung nicht aktiviert",
        "Enable automatic billing to manage your subscription and payment method.": "Aktivieren Sie die automatische Abrechnung, um Ihr Abonnement und Ihre Zahlungsmethode zu verwalten.",
        "Enable auto-billing": "Automatische Abrechnung aktivieren",
        "Auto-billing enabled successfully": "Automatische Abrechnung erfolgreich aktiviert",
        "Failed to enable auto-billing": "Fehler beim Aktivieren der automatischen Abrechnung",
        "Click the Save button to save your payment method.": "Klicken Sie auf die Speichern-Schaltfläche, um Ihre Zahlungsmethode zu speichern.",
        "Click the Save button to save your changes.": "Klicken Sie auf die Speichern-Schaltfläche, um Ihre Änderungen zu speichern.",
        "Billing Address": "Rechnungsadresse",
        "Company name": "Firmenname",
        "Invoice Email": "Rechnungs-E-Mail",
        "VAT ID (e.g. DE123456789)": "USt-IdNr. (z.B. DE123456789)",
        "This is your account email and cannot be changed here.": "Dies ist Ihre Konto-E-Mail-Adresse und kann hier nicht geändert werden.",
        "With an address of its own this web presence gets a separate invoice. The email entered here receives it.": "Mit einer eigenen Adresse erhält dieser Webauftritt eine eigene Rechnung. Sie geht an die hier eingetragene E-Mail-Adresse.",
        "Without an address of its own this web presence is invoiced together with the other web presences of the same owner that have no address of their own. The owner receives one invoice covering all of them.": "Ohne eigene Adresse wird dieser Webauftritt gemeinsam mit den übrigen Webauftritten desselben Inhabers abgerechnet, die keine eigene Adresse haben. Der Inhaber erhält eine Rechnung über alle.",
        "Failed to save billing settings": "Fehler beim Speichern der Rechnungseinstellungen",
        "visa": "Visa",
        "mastercard": "Mastercard",
        "amex": "American Express",
        "discover": "Discover",
        "diners": "Diners Club",
        "jcb": "JCB",
        "unionpay": "UnionPay",
        "sepa_debit": "SEPA-Lastschrift",
        "expires {date}": "gültig bis {date}",
        "expired {date}": "abgelaufen {date}",
        "Invoices": "Rechnungen",
        "Invoice": "Rechnung",
        "Period": "Zeitraum",
        "Total": "Summe",
        "Status": "Status",
        "Download PDF": "PDF herunterladen",
        "Fallback address": "Fallback-Adresse",
        "No address available": "Keine Adresse vorhanden",
        "Add billing address": "Rechnungsadresse hinzufügen",
        "Remove": "Entfernen"
    }
}
</i18n>

<script>
import _ from 'lodash';
import { useSetupStore, useSettingsStore } from '@/stores';
import BillingApi from '@/store/api/billing';
import { openWithFreshToken } from '@/helpers';
import { timestamp2date } from '@/filters';
import AddressCardInput from '@/components/AddressCardInput';

const STRIPE_PUBLIC_KEY = process.env.VUE_APP_STRIPE_PUBLIC_KEY || '';

export default {
    name: 'BillingSettings',
    components: {
        AddressCardInput
    },
    data() {
        return {
            saving: false,
            enabling: false,
            // Stripe
            stripe: null,
            elements: null,
            paymentElement: null,
            stripeLoading: false,
            stripePaymentComplete: false,
            stripePaymentTouched: false,
            stripeError: null,
            stripeSetupIntent: null,
            changingCard: false,
            addingCard: false,
            showBillingAddressForm: false,
            invoices: [],
            loadingInvoices: false
        };
    },
    computed: {
        setupStore() {
            return useSetupStore();
        },
        activeCms() {
            return this.setupStore.activeCms;
        },
        cmsId() {
            const routeId = _.get(this.$route, 'params.id');
            if (routeId) {
                return parseInt(routeId);
            }
            return _.get(this.activeCms, 'id');
        },
        locale() {
            return this.setupStore.locale || 'en';
        },
        // The settings form holds the CMS addressed by the route, which is not
        // necessarily the active one (superuser editing another CMS).
        cms() {
            return this.settingsForm && this.settingsForm.id ? this.settingsForm : this.activeCms;
        },
        billingProfile() {
            return _.get(this.cms, 'stripeBillingProfile');
        },
        // Decided by us, never here: the customer only sees that it applies.
        paysByInvoice() {
            return _.get(this.cms, 'billing_payment_method') === 'invoice';
        },
        isActiveCms() {
            return !!this.cmsId && this.cmsId === _.get(this.activeCms, 'id');
        },
        // The address we hand to Stripe: billing address first, CMS address as fallback.
        stripeBillingAddress() {
            const billingAddress = _.get(this.settingsForm, 'billingAddress') || {};
            const source = billingAddress.street || billingAddress.city
                ? billingAddress
                : (_.get(this.settingsForm, 'address') || {});

            // With fields.billingDetails.address = 'never' Stripe requires every
            // subfield to be present on confirmation, including the unused ones.
            return {
                line1: source.street || '',
                line2: '',
                postal_code: source.zipcode || '',
                city: source.city || '',
                state: '',
                country: source.country || 'DE'
            };
        },
        hasPaymentMethod() {
            return !!(this.billingProfile && this.billingProfile.card_last_four);
        },
        hasBillingProfile() {
            return !!this.billingProfile;
        },
        loading() {
            return !this.cms;
        },
        showStripeForm() {
            return this.changingCard || this.addingCard;
        },
        hasPendingCardData() {
            return this.showStripeForm && this.stripePaymentComplete && !this.stripeValidationError;
        },
        paymentMethodLabel() {
            const brand = _.get(this.billingProfile, 'card_brand', '');
            return this.$t(brand) || brand;
        },
        paymentMethodIcon() {
            const brand = _.get(this.billingProfile, 'card_brand', '');
            const iconMap = {
                'visa': 'el-icon-fa-cc-visa fab',
                'mastercard': 'el-icon-fa-cc-mastercard fab',
                'amex': 'el-icon-fa-cc-amex fab',
                'discover': 'el-icon-fa-cc-discover fab',
                'diners': 'el-icon-fa-cc-diners-club fab',
                'jcb': 'el-icon-fa-cc-jcb fab',
                'sepa_debit': 'el-icon-fa-money-check fas'
            };
            return iconMap[brand] || 'el-icon-fa-credit-card fas';
        },
        cardExpiry() {
            const month = _.get(this.billingProfile, 'card_exp_month');
            const year = _.get(this.billingProfile, 'card_exp_year');
            if (!month || !year) return null;
            return `${String(month).padStart(2, '0')}/${String(year).slice(-2)}`;
        },
        cardExpired() {
            const expiry = this.cardExpiryDate;
            return !!expiry && expiry.getTime() < Date.now();
        },
        cardExpiresSoon() {
            const expiry = this.cardExpiryDate;
            if (!expiry || this.cardExpired) return false;
            return expiry.getTime() - Date.now() < 60 * 86400 * 1000;
        },
        // Cards are valid through the end of their expiry month.
        cardExpiryDate() {
            const month = _.get(this.billingProfile, 'card_exp_month');
            const year = _.get(this.billingProfile, 'card_exp_year');
            if (!month || !year) return null;
            return new Date(year, month, 1);
        },
        stripeValidationError() {
            if (this.stripeError) {
                return this.stripeError;
            }
            if (this.stripePaymentTouched && !this.stripePaymentComplete) {
                return this.$t('Please enter your payment details.');
            }
            return null;
        },
        trialStatusClass() {
            if (this.trialStatus.isExpired) {
                return 'trial-expired';
            }
            if (this.trialStatus.daysRemaining <= 3) {
                return 'trial-warning';
            }
            return 'trial-active';
        },
        trialStatusTitle() {
            if (this.trialStatus.isExpired) {
                return this.$t('Trial Expired');
            }
            return this.$t('Trial Period');
        },
        trialStatusSubtitle() {
            if (this.trialStatus.isExpired) {
                return this.$t('Please add a payment method to continue using the service.');
            }
            // return `${this.trialStatus.daysRemaining} ${this.$t('days remaining')}`;
            return this.$t('{n} days remaining', { n: this.trialStatus.daysRemaining })
        },
        hasBillingAddress() {
            const billingAddress = _.get(this.settingsForm, 'billingAddress');
            if (!billingAddress) return false;
            // Check if any meaningful field has a value
            return !!(
                billingAddress.gender ||
                billingAddress.first_name ||
                billingAddress.last_name ||
                billingAddress.email ||
                billingAddress.company ||
                billingAddress.tax_id ||
                billingAddress.street ||
                billingAddress.city ||
                billingAddress.zipcode
            );
        },
        trialStatus() {
            const trialEndsAt = _.get(this.cms, 'trial_ends_at');
            if (!trialEndsAt) {
                return { isInTrial: false, isExpired: false, daysRemaining: 0 };
            }
            const now = Math.floor(Date.now() / 1000);
            const daysRemaining = Math.ceil((trialEndsAt - now) / 86400);
            return {
                isInTrial: daysRemaining > 0,
                isExpired: daysRemaining <= 0,
                daysRemaining: Math.max(0, daysRemaining)
            };
        },
        settingsForm() {
            return useSettingsStore().form;
        },
        fallbackName() {
            // Try CMS address name first, then owner user name
            const address = _.get(this.settingsForm, 'address');
            if (address) {
                const parts = [address.first_name, address.last_name].filter(Boolean);
                if (parts.length) return parts.join(' ');
                if (address.company) return address.company;
            }

            const ownerName = _.get(this.cms, 'owner.user.name');
            return ownerName || '';
        },
        fallbackAddressOneLiner() {
            const address = _.get(this.settingsForm, 'address');
            if (!address) return '';

            const parts = [];
            if (address.street) parts.push(address.street);
            if (address.zipcode || address.city) {
                parts.push([address.zipcode, address.city].filter(Boolean).join(' '));
            }
            return parts.join(', ');
        },
        emailPlaceholder() {
            // Only email has a fallback - owner's email
            const ownerEmail = _.get(this.cms, 'owner.user.email') || '';
            return {
                email: ownerEmail
            };
        },
        billingScopeNotice() {
            return (this.hasBillingAddress || this.showBillingAddressForm)
                ? this.$t('With an address of its own this web presence gets a separate invoice. The email entered here receives it.')
                : this.$t('Without an address of its own this web presence is invoiced together with the other web presences of the same owner that have no address of their own. The owner receives one invoice covering all of them.');
        }
    },
    watch: {
        cmsId: {
            immediate: true,
            handler() {
                // Reset Stripe form state when switching CMS
                this.cancelChangingCard();
                this.showBillingAddressForm = false;
                this.loadInvoices();
            }
        },
        hasBillingProfile(hasProfile) {
            if (hasProfile && !this.invoices.length) {
                this.loadInvoices();
            }
        },
        paysByInvoice(paysByInvoice) {
            if (paysByInvoice && !this.invoices.length) {
                this.loadInvoices();
            }
        }
    },
    beforeDestroy() {
        this.destroyStripe();
    },
    methods: {
        startAddingCard() {
            this.addingCard = true;
            this.$nextTick(() => {
                this.initStripe();
            });
        },

        startChangingCard() {
            this.changingCard = true;
            this.$nextTick(() => {
                this.initStripe();
            });
        },

        cancelChangingCard() {
            this.changingCard = false;
            this.addingCard = false;
            this.destroyStripe();
            this.resetStripeState();
        },

        applyBillingProfile(billingProfile) {
            this.$set(this.settingsForm, 'stripeBillingProfile', billingProfile);

            if (this.isActiveCms) {
                this.setupStore.activeCms.stripeBillingProfile = billingProfile;
            }
        },

        async loadInvoices() {
            // A bank-transfer customer has invoices without ever having a Stripe profile.
            if (!this.cmsId || (!this.hasBillingProfile && !this.paysByInvoice)) {
                this.invoices = [];
                return;
            }

            this.loadingInvoices = true;

            try {
                this.invoices = await BillingApi.fetchInvoices(this.cmsId);
            } catch (error) {
                console.error('Failed to load invoices:', error);
                this.invoices = [];
            } finally {
                this.loadingInvoices = false;
            }
        },

        invoicePeriodLabel(invoice) {
            const startsAt = _.get(invoice, 'billing_period.starts_at');
            const endsAt = _.get(invoice, 'billing_period.ends_at');

            if (!startsAt || !endsAt) {
                return this.formatDate(_.get(invoice, 'created_at'));
            }

            return `${this.formatDate(startsAt)} - ${this.formatDate(endsAt)}`;
        },

        paymentReason(code) {
            const key = 'payment_reason.' + code;
            const label = this.$t(key);
            return label === key ? code : label;
        },
        failureText(invoice) {
            const known = invoice.failure_code && this.paymentReason(invoice.failure_code) !== invoice.failure_code;
            return known ? this.paymentReason(invoice.failure_code) : (invoice.failure_reason || invoice.failure_code);
        },
        invoiceStatusType(status) {
            const typeMap = {
                paid: 'success',
                pending: 'warning',
                failed: 'danger',
                disputed: 'danger',
                refunded: 'warning'
            };
            return typeMap[status] || 'info';
        },

        openInvoicePdf(invoice) {
            openWithFreshToken((token) => BillingApi.billingInvoicePdfUrl(invoice.id, token));
        },

        formatDate(timestamp) {
            if (!timestamp) return '';
            return timestamp2date(timestamp);
        },

        formatPrice(price) {
            const amount = parseFloat(price) || 0;
            return amount.toLocaleString(this.locale, { style: 'currency', currency: 'EUR' });
        },

        resetStripeState() {
            this.stripePaymentComplete = false;
            this.stripePaymentTouched = false;
            this.stripeError = null;
            this.stripeSetupIntent = null;
        },

        async initStripe() {
            if (!STRIPE_PUBLIC_KEY) {
                console.error('Stripe public key is not configured');
                this.stripeError = 'Payment system is not configured. Please contact support.';
                return;
            }

            this.stripeLoading = true;

            try {
                if (!window.Stripe) {
                    await this.loadStripeScript();
                }

                this.stripe = window.Stripe(STRIPE_PUBLIC_KEY, {
                    locale: this.locale
                });

                // Create setup intent
                const setupIntentData = await BillingApi.createStripeSetupIntent(this.cmsId);
                this.stripeSetupIntent = setupIntentData;

                this.elements = this.stripe.elements({
                    clientSecret: setupIntentData.client_secret,
                    locale: this.locale,
                    appearance: {
                        variables: {
                            fontSizeBase: '14px',
                            colorText: '#303133',
                            colorDanger: '#F56C6C',
                            fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif'
                        }
                    }
                });

                const billingAddress = _.get(this.settingsForm, 'billingAddress') || {};

                this.paymentElement = this.elements.create('payment', {
                    // The billing address is ours (billing address, or the CMS/owner
                    // address as fallback) and is passed on confirmation, so the
                    // element must not collect a second, diverging one.
                    fields: {
                        billingDetails: {
                            address: 'never'
                        }
                    },
                    defaultValues: {
                        billingDetails: {
                            name: billingAddress.company || '',
                            email: billingAddress.email || _.get(this.cms, 'owner.user.email') || ''
                        }
                    }
                });

                await this.$nextTick();
                if (this.$refs.paymentElement) {
                    this.paymentElement.mount(this.$refs.paymentElement);
                }

                this.paymentElement.on('change', this.handlePaymentChange);

                this.stripeLoading = false;
            } catch (error) {
                console.error('Failed to initialize Stripe:', error);
                this.stripeError = 'Failed to load payment form. Please refresh the page.';
                this.stripeLoading = false;
            }
        },

        loadStripeScript() {
            return new Promise((resolve, reject) => {
                if (window.Stripe) {
                    resolve();
                    return;
                }

                const script = document.createElement('script');
                script.src = 'https://js.stripe.com/v3/';
                script.async = true;
                script.onload = resolve;
                script.onerror = () => reject(new Error('Failed to load Stripe.js'));
                document.head.appendChild(script);
            });
        },

        destroyStripe() {
            if (this.paymentElement) {
                this.paymentElement.unmount();
                this.paymentElement = null;
            }
            this.elements = null;
            this.stripe = null;
        },

        handlePaymentChange(event) {
            this.stripePaymentComplete = event.complete;

            // The Payment Element also emits change on mount and on tab switches,
            // so only an actual entry counts as touched.
            if (!event.empty) {
                this.stripePaymentTouched = true;
            }

            if (event.error) {
                this.stripeError = event.error.message;
            } else {
                this.stripeError = null;
            }
        },

        async confirmPendingCard() {
            // Only handle Stripe payment method confirmation - billing address is saved by parent form
            if (!this.hasPendingCardData) {
                return true; // No payment method to save
            }

            this.saving = true;

            try {
                const { setupIntent: confirmedIntent, error } = await this.stripe.confirmSetup({
                    elements: this.elements,
                    redirect: 'if_required',
                    confirmParams: {
                        payment_method_data: {
                            billing_details: {
                                address: this.stripeBillingAddress
                            }
                        }
                    }
                });

                if (error) {
                    this.stripeError = error.message;
                    throw new Error(error.message);
                }

                // Save payment method to backend
                const result = await BillingApi.confirmBillingSetup({
                    cmsId: this.cmsId,
                    paymentMethodId: confirmedIntent.payment_method
                });

                if (result.success && result.billing_profile) {
                    this.applyBillingProfile(result.billing_profile);
                    this.changingCard = false;
                    this.addingCard = false;
                    this.destroyStripe();
                    this.resetStripeState();
                }

                return true;
            } catch (error) {
                console.error('Failed to save payment method:', error);
                this.$notify.error({
                    title: this.$t('Settings could not be saved'),
                    message: this.stripeError || this.$t('The payment details could not be saved.'),
                    iconClass: 'fas el-icon-fa-credit-card',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000
                });
                throw error;
            } finally {
                this.saving = false;
            }
        },

        async enableAutoBilling() {
            if (!this.cmsId) {
                return;
            }

            this.enabling = true;

            try {
                const result = await BillingApi.enableAutoBilling(this.cmsId);

                if (result.success) {
                    // Update the store with returned data
                    if (result.cms) {
                        this.$set(this.settingsForm, 'auto_billing_enabled', result.cms.auto_billing_enabled);
                        if (this.isActiveCms) {
                            this.setupStore.activeCms.auto_billing_enabled = result.cms.auto_billing_enabled;
                        }
                    }
                    if (result.billing_profile) {
                        this.applyBillingProfile(result.billing_profile);
                    }

                    this.$notify.success({
                        title: this.$t('Auto-billing enabled successfully'),
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 3000
                    });
                }
            } catch (error) {
                console.error('Failed to enable auto-billing:', error);
                this.$notify.error({
                    title: this.$t('Failed to enable auto-billing'),
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 6000
                });
            } finally {
                this.enabling = false;
            }
        },

        addBillingAddress() {
            const settingsStore = useSettingsStore();
            // Ensure billingAddress has all required fields for Vue reactivity
            settingsStore.form.billingAddress = {
                gender: _.get(settingsStore.form, 'billingAddress.gender', ''),
                first_name: _.get(settingsStore.form, 'billingAddress.first_name', ''),
                last_name: _.get(settingsStore.form, 'billingAddress.last_name', ''),
                email: _.get(settingsStore.form, 'billingAddress.email', ''),
                company: _.get(settingsStore.form, 'billingAddress.company', ''),
                tax_id: _.get(settingsStore.form, 'billingAddress.tax_id', ''),
                street: _.get(settingsStore.form, 'billingAddress.street', ''),
                zipcode: _.get(settingsStore.form, 'billingAddress.zipcode', ''),
                city: _.get(settingsStore.form, 'billingAddress.city', ''),
                country: _.get(settingsStore.form, 'billingAddress.country', ''),
                latitude: _.get(settingsStore.form, 'billingAddress.latitude', null),
                longitude: _.get(settingsStore.form, 'billingAddress.longitude', null)
            };
            this.showBillingAddressForm = true;
        },

        removeBillingAddress() {
            const settingsStore = useSettingsStore();
            settingsStore.form.billingAddress = {
                gender: '',
                first_name: '',
                last_name: '',
                email: '',
                company: '',
                tax_id: '',
                street: '',
                zipcode: '',
                city: '',
                country: '',
                latitude: null,
                longitude: null
            };
            this.showBillingAddressForm = false;
        }
    }
};
</script>

<style lang="sass" scoped>
.billing-settings
    padding: 20px

    .section
        margin-bottom: 30px
        &.no-billing-profile
            margin-bottom: 0

        &:last-child
            margin-bottom: 0

    .section-title
        margin: 0 0 15px 0
        font-size: 16px
        font-weight: 500
        color: #303133

    .trial-banner
        display: flex
        gap: 12px
        padding: 15px
        border-radius: 6px
        margin-bottom: 20px

        > i
            font-size: 20px
            margin-top: 2px

        .trial-info
            display: flex
            flex-direction: column
            gap: 4px

            .trial-title
                font-weight: 500
                font-size: 14px

            .trial-subtitle
                font-size: 13px

        &.trial-active
            background: #ecf5ff
            border: 1px solid #b3d8ff

            > i
                color: #409EFF

            .trial-title
                color: #303133

            .trial-subtitle
                color: #606266

        &.trial-warning
            background: #fdf6ec
            border: 1px solid #f5dab1

            > i
                color: #E6A23C

            .trial-title
                color: #303133

            .trial-subtitle
                color: #606266

        &.trial-expired
            background: #fef0f0
            border: 1px solid #fbc4c4

            > i
                color: #F56C6C

            .trial-title
                color: #303133

            .trial-subtitle
                color: #606266

    .payment-method-card
        display: flex
        justify-content: space-between
        align-items: center
        padding: 15px
        background: #f5f7fa
        border-radius: 6px
        border: 1px solid #ebeef5

        .card-info
            display: flex
            align-items: center
            gap: 12px

            .card-icon
                font-size: 24px
                color: #606266
                width: 40px
                text-align: center

            .card-details
                display: flex
                flex-direction: column
                gap: 2px

                .card-brand
                    font-weight: 500
                    color: #303133
                    font-size: 14px

                .card-number
                    color: #909399
                    font-size: 13px

                .card-expiry
                    color: #909399
                    font-size: 13px

                    > i
                        margin-right: 4px

                    &.is-warning
                        color: #E6A23C

                    &.is-expired
                        color: #F56C6C

    .no-payment-method
        display: flex
        flex-direction: column
        align-items: center
        padding: 30px
        background: #f5f7fa
        border-radius: 6px
        border: 1px dashed #dcdfe6
        text-align: center

        > i
            font-size: 40px
            color: #dcdfe6
            margin-bottom: 15px

        .no-payment-info
            display: flex
            flex-direction: column
            gap: 4px
            margin-bottom: 15px

            .no-payment-title
                font-weight: 500
                color: #606266
                font-size: 14px

            .no-payment-subtitle
                color: #909399
                font-size: 13px

    .stripe-form
        .stripe-card-wrapper
            border: 1px solid #dcdfe6
            border-radius: 4px
            padding: 12px
            background: #fff
            min-height: 40px
            transition: border-color 0.2s

            &.is-complete
                border-color: #67C23A

            &.has-error
                border-color: #F56C6C

            .stripe-loading
                display: flex
                align-items: center
                gap: 8px
                color: #909399
                font-size: 13px

                i
                    font-size: 16px

        .stripe-actions
            display: flex
            justify-content: flex-end
            gap: 10px
            margin-top: 15px

        .save-hint
            display: flex
            align-items: center
            gap: 8px
            margin-top: 12px
            padding: 10px 12px
            background: #f0f9eb
            border: 1px solid #e1f3d8
            border-radius: 4px
            color: #67C23A
            font-size: 13px

            > i
                color: #67C23A

    .invoice-table
        width: 100%

    .invoice-reason
        margin-top: 2px
        font-size: 0.75rem
        line-height: 1.4
        color: #909399

    .billing-info
        padding: 15px
        background: #f5f7fa
        border-radius: 6px
        border: 1px solid #ebeef5

        .info-row
            display: flex
            padding: 8px 0
            border-bottom: 1px solid #ebeef5

            &:last-child
                border-bottom: none
                padding-bottom: 0

            &:first-child
                padding-top: 0

            .info-label
                flex: 0 0 140px
                color: #909399
                font-size: 13px

            .info-value
                flex: 1
                color: #303133
                font-size: 13px

    .billing-edit-hint
        display: flex
        align-items: flex-start
        gap: 8px
        margin-top: 12px
        color: #909399
        font-size: 12px

        > i
            margin-top: 2px
            color: #C0C4CC

    .address-title
        margin: 20px 0 10px 0
        font-size: 14px
        font-weight: 500
        color: #606266

        &.no-top-margin
            margin-top: 0

    .invoice-email-hint
        display: block
        margin-top: 4px
        font-size: 12px
        color: #909399
        line-height: initial

    .no-margin
        margin: 0

    .billing-scope-notice
        margin-bottom: 15px

        ::v-deep .el-alert__title
            white-space: normal
            line-height: 1.4

    .address-card-wrapper
        position: relative

        .remove-btn
            position: absolute
            top: -8px
            right: -8px
            z-index: 10

    .fallback-panel
        background: #f5f7fa
        border-radius: 4px
        padding: 12px 16px
        min-height: 36px
        line-height: 1.3

        .flex-row
            display: flex
            gap: 20px
            align-items: center

            .grow
                flex: 1

        .info
            .fallback-label
                font-size: 0.75rem
                color: #909399
                letter-spacing: 0.5px
                margin-bottom: 4px

            .fallback-name
                font-weight: 500
                color: #303133

            .fallback-address
                color: #606266
                font-size: 0.9rem

            .no-address
                color: #909399
                font-style: italic
</style>
